/** Response validators for the software-billing function, shared so web and native apply one payment-URL and shape rule. */
import type { SoftwareBillingSummary, SoftwareLicenceAllowance, SoftwareLicencePreview } from './softwareLicenceModel';

export const SOFTWARE_PAYMENT_HOSTS: ReadonlySet<string> = new Set(['checkout.stripe.com', 'billing.contractgroup.co.uk', 'invoice.stripe.com', 'billing.stripe.com']);

export interface SoftwareFieldAccess { canUseField: boolean; canManageMembers: boolean; licenceType: 'full' | 'field' | null; paid: boolean }

export class SoftwareBillingError extends Error {
  constructor(message: string, readonly code: string | null = null) {
    super(message);
    this.name = 'SoftwareBillingError';
  }
}

export const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
const isCount = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0;
const isAllowance = (value: unknown): value is SoftwareLicenceAllowance => isRecord(value)
  && isCount(value.purchased) && isCount(value.assigned) && isCount(value.unitAmount);

export function validateSoftwarePaymentUrl(value: unknown): string {
  // Matched on the text rather than new URL(): React Native's URL polyfill is incomplete. The host class excludes ':' and '@', so ports and userinfo cannot match.
  const match = typeof value === 'string' ? /^https:\/\/([a-z0-9.-]+)(\/[^\s?#\\]*)?(\?[^\s#\\]*)?(#[^\s\\]*)?$/i.exec(value) : null;
  if (!match || !SOFTWARE_PAYMENT_HOSTS.has(match[1].toLowerCase())) throw new SoftwareBillingError('We could not open the secure payment page. Please try again.');
  return value as string;
}

export function parseSoftwareSummary(data: unknown): SoftwareBillingSummary | null {
  if (isRecord(data) && data.subscription === null) return null;
  if (!isRecord(data) || typeof data.workspaceId !== 'string' || typeof data.status !== 'string'
    || typeof data.canManageBilling !== 'boolean' || typeof data.cancelAtPeriodEnd !== 'boolean'
    || typeof data.canAddLicences !== 'boolean' || !isAllowance(data.full) || !isAllowance(data.field)
    || data.currency !== 'gbp' || !(data.periodEnd === null || typeof data.periodEnd === 'number' || typeof data.periodEnd === 'string')) {
    throw new SoftwareBillingError('We could not read your software licences. Please refresh and try again.');
  }
  return data as unknown as SoftwareBillingSummary;
}

export function parseSoftwarePreview(data: unknown): SoftwareLicencePreview {
  if (!isRecord(data) || typeof data.previewId !== 'string' || !isCount(data.amountDue) || !isCount(data.monthlyTotal)
    || data.currency !== 'gbp' || !(typeof data.expiresAt === 'string' || typeof data.expiresAt === 'number')) throw new SoftwareBillingError('We could not calculate this change. Please try again.');
  return data as unknown as SoftwareLicencePreview;
}

export function parseSoftwareAccess(data: unknown): SoftwareFieldAccess {
  if (!isRecord(data) || typeof data.canUseField !== 'boolean' || typeof data.canManageMembers !== 'boolean'
    || typeof data.paid !== 'boolean' || !['full', 'field', null].includes(data.licenceType as string | null)) throw new SoftwareBillingError('We could not check your Field access. Please try again.');
  return data as unknown as SoftwareFieldAccess;
}

export function parseSoftwareConfirm(data: unknown): { summary: SoftwareBillingSummary | null; paymentUrl?: string; pending: boolean } {
  if (!isRecord(data)) throw new SoftwareBillingError('We could not confirm this change. Please refresh your licences.');
  return {
    summary: data.summary === undefined ? null : parseSoftwareSummary(data.summary),
    pending: data.pending === true,
    ...(data.paymentUrl ? { paymentUrl: validateSoftwarePaymentUrl(data.paymentUrl) } : {}),
  };
}
